/**
 * Emoji support for the messaging composers (inquiries, replies, feedback):
 * a searchable catalog for the picker, recently-used ordering, and automatic
 * conversion of typed shortcuts (`:)` → 🙂, `:thumbsup:` → 👍) that the user
 * can undo with Backspace to keep the characters they typed.
 *
 * Pure functions only — the web and mobile composers apply them to their own
 * editors and keep recents in their own storage.
 */

export enum EmojiCategory {
  Smileys = 'smileys',
  People = 'people',
  Hearts = 'hearts',
  Nature = 'nature',
  Food = 'food',
  Activities = 'activities',
  Travel = 'travel',
  Objects = 'objects',
  Symbols = 'symbols',
}

export const EMOJI_CATEGORY_LABELS: Record<EmojiCategory, string> = {
  [EmojiCategory.Smileys]: 'Smileys',
  [EmojiCategory.People]: 'People & gestures',
  [EmojiCategory.Hearts]: 'Hearts',
  [EmojiCategory.Nature]: 'Animals & nature',
  [EmojiCategory.Food]: 'Food & drink',
  [EmojiCategory.Activities]: 'Activities',
  [EmojiCategory.Travel]: 'Travel & places',
  [EmojiCategory.Objects]: 'Objects',
  [EmojiCategory.Symbols]: 'Symbols',
};

export interface EmojiEntry {
  emoji: string;
  /** Lower-case name; its underscored form is the `:shortcode:`. */
  name: string;
  /** Extra search words. */
  keywords: string[];
  category: EmojiCategory;
}

type Row = [emoji: string, name: string, keywords?: string];

const RAW_CATALOG: Record<EmojiCategory, Row[]> = {
  [EmojiCategory.Smileys]: [
    ['😀', 'grinning', 'smile happy'],
    ['😃', 'smiley', 'happy joy'],
    ['😄', 'smile', 'happy laugh'],
    ['😁', 'grin', 'happy teeth'],
    ['😆', 'laughing', 'lol haha'],
    ['😅', 'sweat smile', 'relief phew'],
    ['🤣', 'rofl', 'rolling laugh lol'],
    ['😂', 'joy', 'tears laugh lol'],
    ['🙂', 'slight smile', 'happy'],
    ['🙃', 'upside down', 'silly sarcasm'],
    ['😉', 'wink', 'flirt'],
    ['😊', 'blush', 'happy proud'],
    ['😇', 'innocent', 'angel halo'],
    ['🥰', 'smiling with hearts', 'love adore'],
    ['😍', 'heart eyes', 'love crush'],
    ['🤩', 'star struck', 'wow amazing'],
    ['😘', 'kissing heart', 'kiss love'],
    ['😋', 'yum', 'tasty delicious'],
    ['😛', 'tongue', 'playful'],
    ['😜', 'winking tongue', 'crazy playful'],
    ['🤪', 'zany', 'crazy goofy'],
    ['🤑', 'money mouth', 'rich dollar'],
    ['🤗', 'hugging', 'hug'],
    ['🤭', 'hand over mouth', 'oops giggle'],
    ['🤫', 'shushing', 'quiet secret'],
    ['🤔', 'thinking', 'hmm consider'],
    ['🤐', 'zipper mouth', 'secret quiet'],
    ['🤨', 'raised eyebrow', 'skeptical doubt'],
    ['😐', 'neutral', 'meh'],
    ['😑', 'expressionless', 'blank'],
    ['😶', 'no mouth', 'speechless'],
    ['😏', 'smirk', 'smug'],
    ['😒', 'unamused', 'meh annoyed'],
    ['🙄', 'eye roll', 'whatever'],
    ['😬', 'grimacing', 'awkward eek'],
    ['😌', 'relieved', 'calm content'],
    ['😔', 'pensive', 'sad thoughtful'],
    ['😪', 'sleepy', 'tired'],
    ['😴', 'sleeping', 'zzz tired'],
    ['😷', 'mask', 'sick ill'],
    ['🤒', 'thermometer face', 'sick fever'],
    ['🤢', 'nauseated', 'sick gross'],
    ['🤧', 'sneezing', 'sick cold'],
    ['🥵', 'hot face', 'heat sweat'],
    ['🥶', 'cold face', 'freezing'],
    ['🥴', 'woozy', 'dizzy'],
    ['🤯', 'exploding head', 'mind blown shocked'],
    ['🥳', 'partying', 'party celebrate birthday'],
    ['😎', 'sunglasses', 'cool'],
    ['🤓', 'nerd', 'geek'],
    ['🧐', 'monocle', 'inspect curious'],
    ['😕', 'confused', 'unsure'],
    ['😟', 'worried', 'concerned'],
    ['🙁', 'slight frown', 'sad'],
    ['😮', 'open mouth', 'surprised wow'],
    ['😯', 'hushed', 'surprised'],
    ['😲', 'astonished', 'shocked'],
    ['😳', 'flushed', 'embarrassed'],
    ['🥺', 'pleading', 'please puppy eyes'],
    ['😦', 'frowning', 'shocked'],
    ['😨', 'fearful', 'scared'],
    ['😰', 'anxious', 'nervous sweat'],
    ['😥', 'sad relieved', 'disappointed'],
    ['😢', 'cry', 'sad tear'],
    ['😭', 'sob', 'crying sad'],
    ['😱', 'scream', 'shock fear'],
    ['😖', 'confounded', 'frustrated'],
    ['😣', 'persevere', 'struggle'],
    ['😞', 'disappointed', 'sad'],
    ['😓', 'downcast sweat', 'tired'],
    ['😩', 'weary', 'tired frustrated'],
    ['😫', 'tired face', 'exhausted'],
    ['🥱', 'yawning', 'bored tired'],
    ['😤', 'triumph', 'huff proud'],
    ['😡', 'rage', 'angry mad'],
    ['😠', 'angry', 'mad'],
    ['🤬', 'cursing', 'swear angry'],
    ['😈', 'smiling imp', 'devil mischief'],
    ['💀', 'skull', 'dead lol'],
    ['💩', 'poop', 'poo'],
    ['🤡', 'clown', 'silly'],
    ['👻', 'ghost', 'boo'],
    ['👽', 'alien', 'ufo'],
    ['🤖', 'robot', 'bot ai'],
    ['😺', 'smiley cat', 'cat happy'],
    ['🙈', 'see no evil', 'monkey oops'],
    ['🙉', 'hear no evil', 'monkey'],
    ['🙊', 'speak no evil', 'monkey secret'],
  ],
  [EmojiCategory.People]: [
    ['👍', 'thumbs up', 'yes like approve ok +1'],
    ['👎', 'thumbs down', 'no dislike -1'],
    ['👌', 'ok hand', 'okay perfect'],
    ['✌️', 'victory', 'peace'],
    ['🤞', 'crossed fingers', 'luck hope'],
    ['🤟', 'love you gesture', 'ily'],
    ['🤘', 'rock on', 'metal horns'],
    ['🤙', 'call me', 'shaka'],
    ['👈', 'point left', 'left'],
    ['👉', 'point right', 'right'],
    ['👆', 'point up', 'up'],
    ['👇', 'point down', 'down'],
    ['☝️', 'index up', 'one attention'],
    ['✋', 'raised hand', 'stop high five'],
    ['🖐️', 'hand splayed', 'five'],
    ['👋', 'wave', 'hello hi bye'],
    ['👏', 'clap', 'applause bravo'],
    ['🙌', 'raised hands', 'celebrate hooray'],
    ['👐', 'open hands', 'hug'],
    ['🤲', 'palms up', 'pray offer'],
    ['🤝', 'handshake', 'deal agreement'],
    ['🙏', 'pray', 'please thanks thank you hope'],
    ['✍️', 'writing hand', 'write sign'],
    ['💪', 'muscle', 'strong flex'],
    ['🫡', 'saluting', 'respect yes sir'],
    ['👀', 'eyes', 'look see watching'],
    ['🧠', 'brain', 'smart think'],
    ['👶', 'baby', 'child newborn'],
    ['🧒', 'child', 'kid'],
    ['👦', 'boy', 'kid'],
    ['👧', 'girl', 'kid'],
    ['🧑', 'person', 'adult'],
    ['👨', 'man', 'adult'],
    ['👩', 'woman', 'adult'],
    ['🧓', 'older person', 'elder'],
    ['👴', 'old man', 'grandfather elder'],
    ['👵', 'old woman', 'grandmother elder'],
    ['🧑‍💻', 'technologist', 'developer coder computer'],
    ['🧑‍🏫', 'teacher', 'school educator'],
    ['🧑‍🎓', 'student', 'graduate school'],
    ['🧑‍⚕️', 'health worker', 'doctor nurse'],
    ['🧑‍🍳', 'cook', 'chef'],
    ['🧑‍💼', 'office worker', 'business manager'],
    ['🕺', 'dancing man', 'dance party'],
    ['💃', 'dancing woman', 'dance party'],
    ['🏃', 'running', 'run hurry'],
    ['🚶', 'walking', 'walk'],
    ['🤷', 'shrug', 'dunno whatever'],
    ['🤦', 'facepalm', 'ugh doh'],
    ['🙋', 'raising hand', 'me question'],
    ['🙆', 'ok gesture', 'yes'],
    ['🙅', 'no gesture', 'stop no'],
    ['💁', 'tipping hand', 'info sassy'],
    ['👪', 'family', 'parents kids'],
    ['👥', 'people', 'users team group'],
  ],
  [EmojiCategory.Hearts]: [
    ['❤️', 'red heart', 'love heart'],
    ['🧡', 'orange heart', 'love'],
    ['💛', 'yellow heart', 'love'],
    ['💚', 'green heart', 'love'],
    ['💙', 'blue heart', 'love'],
    ['💜', 'purple heart', 'love'],
    ['🖤', 'black heart', 'love'],
    ['🤍', 'white heart', 'love'],
    ['🤎', 'brown heart', 'love'],
    ['💔', 'broken heart', 'sad heartbreak'],
    ['❣️', 'heart exclamation', 'love'],
    ['💕', 'two hearts', 'love'],
    ['💞', 'revolving hearts', 'love'],
    ['💓', 'beating heart', 'love'],
    ['💗', 'growing heart', 'love'],
    ['💖', 'sparkling heart', 'love'],
    ['💘', 'cupid', 'love arrow'],
    ['💝', 'heart gift', 'love present'],
    ['💯', '100', 'hundred perfect score'],
    ['💋', 'kiss mark', 'lips'],
  ],
  [EmojiCategory.Nature]: [
    ['🐶', 'dog', 'puppy pet'],
    ['🐱', 'cat', 'kitten pet'],
    ['🐭', 'mouse', 'animal'],
    ['🐰', 'rabbit', 'bunny'],
    ['🦊', 'fox', 'animal'],
    ['🐻', 'bear', 'animal'],
    ['🐼', 'panda', 'animal'],
    ['🐨', 'koala', 'animal'],
    ['🐯', 'tiger', 'animal'],
    ['🦁', 'lion', 'animal king'],
    ['🐮', 'cow', 'animal'],
    ['🐷', 'pig', 'animal'],
    ['🐸', 'frog', 'animal'],
    ['🐵', 'monkey', 'animal'],
    ['🐔', 'chicken', 'animal'],
    ['🐧', 'penguin', 'animal'],
    ['🐦', 'bird', 'animal'],
    ['🦅', 'eagle', 'bird'],
    ['🦉', 'owl', 'bird wise'],
    ['🐴', 'horse', 'animal'],
    ['🦄', 'unicorn', 'magic'],
    ['🐝', 'bee', 'busy honey'],
    ['🦋', 'butterfly', 'insect'],
    ['🐢', 'turtle', 'slow'],
    ['🐍', 'snake', 'animal'],
    ['🐙', 'octopus', 'sea'],
    ['🐬', 'dolphin', 'sea'],
    ['🐳', 'whale', 'sea'],
    ['🐟', 'fish', 'sea'],
    ['🌸', 'cherry blossom', 'flower spring'],
    ['🌹', 'rose', 'flower'],
    ['🌻', 'sunflower', 'flower'],
    ['🌷', 'tulip', 'flower'],
    ['💐', 'bouquet', 'flowers'],
    ['🌱', 'seedling', 'plant grow'],
    ['🌳', 'tree', 'nature'],
    ['🌴', 'palm tree', 'beach'],
    ['🌵', 'cactus', 'desert'],
    ['🍀', 'four leaf clover', 'luck'],
    ['🍁', 'maple leaf', 'fall autumn'],
    ['🌞', 'sun with face', 'sunny'],
    ['☀️', 'sun', 'sunny weather'],
    ['🌙', 'crescent moon', 'night'],
    ['⭐', 'star', 'favorite'],
    ['🌟', 'glowing star', 'shine'],
    ['✨', 'sparkles', 'shiny magic new'],
    ['⚡', 'zap', 'lightning fast'],
    ['🔥', 'fire', 'hot lit'],
    ['🌈', 'rainbow', 'weather'],
    ['☁️', 'cloud', 'weather'],
    ['🌧️', 'rain', 'weather'],
    ['⛈️', 'storm', 'thunder weather'],
    ['❄️', 'snowflake', 'cold winter'],
    ['☃️', 'snowman', 'winter'],
    ['🌊', 'wave', 'ocean sea'],
    ['💧', 'droplet', 'water'],
    ['🌍', 'earth', 'globe world'],
  ],
  [EmojiCategory.Food]: [
    ['🍎', 'apple', 'fruit'],
    ['🍊', 'orange', 'fruit'],
    ['🍋', 'lemon', 'fruit'],
    ['🍌', 'banana', 'fruit'],
    ['🍉', 'watermelon', 'fruit'],
    ['🍇', 'grapes', 'fruit'],
    ['🍓', 'strawberry', 'fruit'],
    ['🍒', 'cherries', 'fruit'],
    ['🍑', 'peach', 'fruit'],
    ['🍍', 'pineapple', 'fruit'],
    ['🥑', 'avocado', 'fruit'],
    ['🥕', 'carrot', 'vegetable'],
    ['🌽', 'corn', 'vegetable'],
    ['🥦', 'broccoli', 'vegetable'],
    ['🍞', 'bread', 'challah'],
    ['🥐', 'croissant', 'bakery'],
    ['🥯', 'bagel', 'bakery'],
    ['🧀', 'cheese', 'dairy'],
    ['🥚', 'egg', 'breakfast'],
    ['🍳', 'cooking', 'fried egg breakfast'],
    ['🥞', 'pancakes', 'breakfast'],
    ['🍕', 'pizza', 'food'],
    ['🍔', 'hamburger', 'burger'],
    ['🍟', 'fries', 'food'],
    ['🌭', 'hot dog', 'food'],
    ['🌮', 'taco', 'food'],
    ['🥗', 'salad', 'healthy'],
    ['🍝', 'spaghetti', 'pasta'],
    ['🍜', 'ramen', 'noodles soup'],
    ['🍣', 'sushi', 'fish'],
    ['🍿', 'popcorn', 'movie'],
    ['🍩', 'doughnut', 'donut sufganiyah'],
    ['🍪', 'cookie', 'dessert'],
    ['🎂', 'birthday cake', 'party celebrate'],
    ['🍰', 'cake', 'dessert'],
    ['🧁', 'cupcake', 'dessert'],
    ['🍫', 'chocolate', 'dessert'],
    ['🍬', 'candy', 'sweet'],
    ['🍯', 'honey', 'sweet rosh hashana'],
    ['☕', 'coffee', 'tea hot drink'],
    ['🍵', 'tea', 'hot drink'],
    ['🥤', 'cup with straw', 'soda drink'],
    ['🧃', 'juice box', 'drink'],
    ['🍷', 'wine', 'drink kiddush'],
    ['🥂', 'clinking glasses', 'cheers lchaim toast'],
    ['🍾', 'champagne', 'celebrate'],
    ['🥛', 'milk', 'drink dairy'],
  ],
  [EmojiCategory.Activities]: [
    ['🎉', 'tada', 'party celebrate congrats hooray'],
    ['🎊', 'confetti', 'party celebrate'],
    ['🎈', 'balloon', 'party birthday'],
    ['🎁', 'gift', 'present birthday'],
    ['🏆', 'trophy', 'win award champion'],
    ['🥇', 'first place', 'gold medal winner'],
    ['🥈', 'second place', 'silver medal'],
    ['🥉', 'third place', 'bronze medal'],
    ['🏅', 'medal', 'award'],
    ['⚽', 'soccer', 'ball sport'],
    ['🏀', 'basketball', 'ball sport'],
    ['🏈', 'football', 'ball sport'],
    ['⚾', 'baseball', 'ball sport'],
    ['🎾', 'tennis', 'ball sport'],
    ['🏐', 'volleyball', 'ball sport'],
    ['🎯', 'bullseye', 'target goal dart'],
    ['🎮', 'video game', 'gaming controller'],
    ['🎲', 'dice', 'game'],
    ['🧩', 'puzzle', 'piece'],
    ['🎨', 'art', 'paint palette'],
    ['🎭', 'theater', 'drama'],
    ['🎬', 'clapper', 'movie film'],
    ['🎤', 'microphone', 'sing speak'],
    ['🎧', 'headphones', 'music'],
    ['🎵', 'music', 'note song'],
    ['🎶', 'notes', 'music song'],
    ['🎹', 'piano', 'music'],
    ['🎸', 'guitar', 'music'],
    ['🥁', 'drum', 'music'],
    ['🎻', 'violin', 'music'],
    ['📚', 'books', 'study library'],
    ['🏋️', 'weight lifting', 'gym exercise'],
    ['🚴', 'biking', 'bicycle'],
    ['🏊', 'swimming', 'swim'],
    ['🧘', 'yoga', 'meditate calm'],
  ],
  [EmojiCategory.Travel]: [
    ['🚗', 'car', 'drive'],
    ['🚕', 'taxi', 'cab'],
    ['🚌', 'bus', 'transit'],
    ['🚑', 'ambulance', 'emergency'],
    ['🚒', 'fire engine', 'emergency'],
    ['🚓', 'police car', 'cop'],
    ['🚚', 'truck', 'delivery'],
    ['🚲', 'bicycle', 'bike'],
    ['🛴', 'scooter', 'kick'],
    ['🚂', 'train', 'locomotive'],
    ['🚇', 'metro', 'subway'],
    ['✈️', 'airplane', 'flight travel trip'],
    ['🛫', 'departure', 'flight takeoff'],
    ['🛬', 'arrival', 'flight landing'],
    ['🚀', 'rocket', 'launch ship fast'],
    ['🛸', 'flying saucer', 'ufo'],
    ['🚁', 'helicopter', 'fly'],
    ['⛵', 'sailboat', 'boat'],
    ['🚢', 'ship', 'boat cruise'],
    ['⚓', 'anchor', 'ship'],
    ['⛽', 'fuel', 'gas station'],
    ['🚧', 'construction', 'work in progress'],
    ['🚦', 'traffic light', 'signal'],
    ['🗺️', 'map', 'world travel'],
    ['🧭', 'compass', 'direction'],
    ['🏠', 'house', 'home'],
    ['🏡', 'house with garden', 'home'],
    ['🏢', 'office building', 'work'],
    ['🏫', 'school', 'education'],
    ['🏥', 'hospital', 'medical'],
    ['🏦', 'bank', 'money finance'],
    ['🏨', 'hotel', 'stay'],
    ['🕍', 'synagogue', 'shul temple'],
    ['⛺', 'tent', 'camping'],
    ['🏖️', 'beach', 'vacation'],
    ['🏔️', 'mountain', 'snow'],
    ['🌆', 'city', 'dusk'],
    ['🌃', 'night', 'city stars'],
    ['🗽', 'statue of liberty', 'new york'],
  ],
  [EmojiCategory.Objects]: [
    ['⌚', 'watch', 'time'],
    ['📱', 'phone', 'mobile cell'],
    ['💻', 'laptop', 'computer'],
    ['🖥️', 'desktop', 'computer monitor'],
    ['⌨️', 'keyboard', 'type'],
    ['🖨️', 'printer', 'print'],
    ['🖱️', 'computer mouse', 'click'],
    ['💾', 'floppy disk', 'save'],
    ['📷', 'camera', 'photo'],
    ['📹', 'video camera', 'record'],
    ['📺', 'television', 'tv'],
    ['📞', 'telephone', 'call'],
    ['☎️', 'phone receiver', 'call'],
    ['🔋', 'battery', 'power'],
    ['🔌', 'plug', 'power electric'],
    ['💡', 'bulb', 'idea light'],
    ['🔦', 'flashlight', 'torch'],
    ['🕯️', 'candle', 'shabbat light'],
    ['💵', 'dollar', 'money cash'],
    ['💰', 'money bag', 'cash rich budget'],
    ['💳', 'credit card', 'payment'],
    ['🧾', 'receipt', 'expense invoice'],
    ['💸', 'money wings', 'spend expense'],
    ['📈', 'chart up', 'growth increase trend'],
    ['📉', 'chart down', 'decrease decline trend'],
    ['📊', 'bar chart', 'stats report'],
    ['📋', 'clipboard', 'list'],
    ['📌', 'pushpin', 'pin location'],
    ['📍', 'round pushpin', 'location place'],
    ['📎', 'paperclip', 'attachment'],
    ['✂️', 'scissors', 'cut'],
    ['🖊️', 'pen', 'write'],
    ['✏️', 'pencil', 'write edit'],
    ['📝', 'memo', 'note write'],
    ['📁', 'folder', 'file directory'],
    ['📂', 'open folder', 'file directory'],
    ['📄', 'page', 'document file'],
    ['📅', 'calendar', 'date schedule'],
    ['📆', 'tear off calendar', 'date schedule'],
    ['🗓️', 'spiral calendar', 'date schedule'],
    ['📦', 'package', 'box delivery'],
    ['📧', 'email', 'mail'],
    ['✉️', 'envelope', 'mail letter'],
    ['📨', 'incoming envelope', 'mail inbox'],
    ['📬', 'mailbox', 'mail'],
    ['📢', 'loudspeaker', 'announcement'],
    ['📣', 'megaphone', 'announcement'],
    ['🔔', 'bell', 'notification alert'],
    ['🔕', 'no bell', 'mute silent'],
    ['🔑', 'key', 'password lock'],
    ['🔒', 'lock', 'secure private'],
    ['🔓', 'unlock', 'open'],
    ['🔨', 'hammer', 'tool build'],
    ['🔧', 'wrench', 'tool fix settings'],
    ['⚙️', 'gear', 'settings'],
    ['🧰', 'toolbox', 'tools'],
    ['🔍', 'magnifying glass', 'search find'],
    ['🔗', 'link', 'url chain'],
    ['⏰', 'alarm clock', 'time wake'],
    ['⏳', 'hourglass', 'time wait'],
    ['⌛', 'hourglass done', 'time'],
    ['🎓', 'graduation cap', 'school degree'],
    ['💼', 'briefcase', 'work business'],
    ['🛒', 'shopping cart', 'buy store'],
    ['🧹', 'broom', 'clean'],
    ['🪔', 'oil lamp', 'chanukah light'],
    ['🕎', 'menorah', 'chanukah hanukkah'],
    ['📜', 'scroll', 'torah document'],
  ],
  [EmojiCategory.Symbols]: [
    ['✅', 'check', 'done yes complete'],
    ['☑️', 'ballot check', 'done'],
    ['✔️', 'check mark', 'done yes'],
    ['❌', 'x', 'no cross wrong cancel'],
    ['❎', 'cross mark button', 'no'],
    ['➕', 'plus', 'add'],
    ['➖', 'minus', 'subtract'],
    ['❓', 'question', 'help'],
    ['❗', 'exclamation', 'important'],
    ['‼️', 'double exclamation', 'important'],
    ['⚠️', 'warning', 'caution alert'],
    ['🚫', 'no entry', 'forbidden prohibited'],
    ['⛔', 'stop sign', 'no entry'],
    ['🆗', 'ok button', 'okay'],
    ['🆕', 'new', 'fresh'],
    ['🆓', 'free', 'gratis'],
    ['🔝', 'top', 'up'],
    ['🔜', 'soon', 'arrow'],
    ['🔴', 'red circle', 'dot'],
    ['🟠', 'orange circle', 'dot'],
    ['🟡', 'yellow circle', 'dot'],
    ['🟢', 'green circle', 'dot'],
    ['🔵', 'blue circle', 'dot'],
    ['🟣', 'purple circle', 'dot'],
    ['⚫', 'black circle', 'dot'],
    ['⚪', 'white circle', 'dot'],
    ['⬆️', 'up arrow', 'arrow'],
    ['⬇️', 'down arrow', 'arrow'],
    ['⬅️', 'left arrow', 'arrow back'],
    ['➡️', 'right arrow', 'arrow next'],
    ['🔄', 'counterclockwise arrows', 'refresh sync repeat'],
    ['🔁', 'repeat', 'loop recurring'],
    ['▶️', 'play', 'start'],
    ['⏸️', 'pause', 'hold'],
    ['⏹️', 'stop', 'end'],
    ['✡️', 'star of david', 'jewish magen david'],
    ['♻️', 'recycle', 'reuse'],
    ['©️', 'copyright', 'c'],
    ['®️', 'registered', 'r'],
    ['™️', 'trademark', 'tm'],
    ['#️⃣', 'hash', 'number pound'],
    ['💲', 'heavy dollar', 'money'],
    ['💬', 'speech balloon', 'comment chat message'],
    ['💭', 'thought balloon', 'think'],
    ['🗨️', 'speech bubble', 'comment'],
    ['💤', 'zzz', 'sleep'],
    ['🆘', 'sos', 'help emergency'],
    ['🏁', 'checkered flag', 'finish done'],
    ['🚩', 'red flag', 'warning'],
    ['🇺🇸', 'us flag', 'usa america'],
    ['🇮🇱', 'israel flag', 'israel'],
  ],
};

function buildCatalog(): EmojiEntry[] {
  const seen = new Set<string>();
  const out: EmojiEntry[] = [];
  for (const category of Object.values(EmojiCategory)) {
    for (const [emoji, name, keywords] of RAW_CATALOG[category]) {
      if (seen.has(emoji)) continue;
      seen.add(emoji);
      out.push({ emoji, name, keywords: keywords ? keywords.split(' ') : [], category });
    }
  }
  return out;
}

/** Every emoji the picker offers, in display order (each emoji once). */
export const EMOJI_CATALOG: readonly EmojiEntry[] = buildCatalog();

/** `name` → its `:shortcode:` body (spaces become underscores). */
export function emojiShortcode(entry: Pick<EmojiEntry, 'name'>): string {
  return entry.name.replace(/\s+/g, '_');
}

/** Common shortcodes other chat apps use that aren't a catalog name. */
const SHORTCODE_ALIASES: Record<string, string> = {
  '+1': '👍',
  '-1': '👎',
  thumbsup: '👍',
  thumbsdown: '👎',
  heart: '❤️',
  laugh: '😆',
  lol: '😂',
  sad: '😢',
  thanks: '🙏',
  ok: '👌',
  party: '🥳',
  check: '✅',
  white_check_mark: '✅',
  heavy_check_mark: '✔️',
  eyes: '👀',
  wave: '👋',
};

const SHORTCODES: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const entry of EMOJI_CATALOG) {
    const code = emojiShortcode(entry);
    if (!map.has(code)) map.set(code, entry.emoji);
  }
  for (const [code, emoji] of Object.entries(SHORTCODE_ALIASES)) map.set(code, emoji);
  return map;
})();

/** The emoji for a `:shortcode:` body (case-insensitive), if any. */
export function emojiForShortcode(code: string): string | undefined {
  return SHORTCODES.get(code.toLowerCase());
}

/**
 * Text emoticons converted when followed by a space or new line. Only ones
 * unlikely to be typed for another reason; each must stand alone (start of
 * text or after whitespace), so `http://` or `(see 3:)` never convert.
 */
export const EMOJI_EMOTICONS: Readonly<Record<string, string>> = {
  ':)': '🙂',
  ':-)': '🙂',
  '(:': '🙂',
  ':D': '😃',
  ':-D': '😃',
  ';)': '😉',
  ';-)': '😉',
  ':(': '🙁',
  ':-(': '🙁',
  ":'(": '😢',
  ':P': '😛',
  ':-P': '😛',
  ':p': '😛',
  ':-p': '😛',
  ':O': '😮',
  ':-O': '😮',
  ':o': '😮',
  ':|': '😐',
  ':-|': '😐',
  ':/': '😕',
  ':-/': '😕',
  ':*': '😘',
  ':-*': '😘',
  '^_^': '😊',
  '<3': '❤️',
  '</3': '💔',
  '(y)': '👍',
  '(Y)': '👍',
};

/** One automatic conversion, kept so Backspace can undo it. */
export interface EmojiConversion {
  /** Offset of the emoji in the converted text. */
  start: number;
  /** What the user typed (`:)` or `:smile:`). */
  original: string;
  emoji: string;
  /** The space/new line that triggered an emoticon ('' for a shortcode). */
  trailing: string;
}

export interface EmojiEditResult {
  text: string;
  caret: number;
}

/**
 * Convert the shortcut the user just finished typing at `caret`, if any:
 * an emoticon once followed by whitespace (`:) ` → `🙂 `), or a shortcode
 * on its closing colon (`:tada:` → `🎉`). `kept` holds shortcuts the user
 * has undone — they stay as typed.
 */
export function convertEmojiShortcut(
  text: string,
  caret: number,
  kept: ReadonlySet<string> = new Set(),
): (EmojiEditResult & { conversion: EmojiConversion }) | null {
  if (caret <= 0 || caret > text.length) return null;
  const before = text.slice(0, caret);
  const last = before[before.length - 1];

  if (/\s/.test(last)) {
    const match = before.slice(0, -1).match(/(?:^|\s)(\S+)$/);
    if (!match) return null;
    const original = match[1];
    const emoji = EMOJI_EMOTICONS[original];
    if (!emoji || kept.has(original)) return null;
    const start = caret - 1 - original.length;
    return {
      text: text.slice(0, start) + emoji + last + text.slice(caret),
      caret: start + emoji.length + 1,
      conversion: { start, original, emoji, trailing: last },
    };
  }

  if (last === ':') {
    const match = before.match(/(?:^|\s)(:([a-z0-9_+-]+):)$/i);
    if (!match) return null;
    const original = match[1];
    const emoji = emojiForShortcode(match[2]);
    if (!emoji || kept.has(original)) return null;
    const start = caret - original.length;
    return {
      text: text.slice(0, start) + emoji + text.slice(caret),
      caret: start + emoji.length,
      conversion: { start, original, emoji, trailing: '' },
    };
  }

  return null;
}

/**
 * Undo `conversion` when the caret is still right after it (Backspace just
 * after a conversion): the emoji turns back into exactly what was typed.
 * Null when the text has moved on, so Backspace deletes as usual.
 */
export function revertEmojiShortcut(
  text: string,
  caret: number,
  conversion: EmojiConversion,
): EmojiEditResult | null {
  const { start, original, emoji, trailing } = conversion;
  const converted = emoji + trailing;
  if (start < 0 || caret !== start + converted.length) return null;
  if (text.slice(start, caret) !== converted) return null;
  const restored = original + trailing;
  return {
    text: text.slice(0, start) + restored + text.slice(caret),
    caret: start + restored.length,
  };
}

/** How many recently used emojis are remembered. */
export const EMOJI_RECENT_LIMIT = 24;

/** Storage key for a user's recently used emojis (per user, per browser). */
export function emojiRecentStorageKey(userId: string | null | undefined): string {
  return `emoji_recent:${userId ?? 'anonymous'}`;
}

/** Read a stored recents list, dropping anything malformed. */
export function parseRecentEmojis(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is string => typeof e === 'string' && e.length > 0).slice(0, EMOJI_RECENT_LIMIT);
  } catch {
    return [];
  }
}

/** `emoji` moved to the front of `recent` (most recent first). */
export function recordRecentEmoji(recent: readonly string[], emoji: string, limit = EMOJI_RECENT_LIMIT): string[] {
  return [emoji, ...recent.filter((e) => e !== emoji)].slice(0, limit);
}

const CATALOG_BY_EMOJI: ReadonlyMap<string, EmojiEntry> = new Map(EMOJI_CATALOG.map((e) => [e.emoji, e]));

/** Catalog entries for a recents list (an emoji no longer listed is shown bare). */
export function recentEmojiEntries(recent: readonly string[]): EmojiEntry[] {
  return recent.map(
    (emoji) => CATALOG_BY_EMOJI.get(emoji) ?? { emoji, name: emoji, keywords: [], category: EmojiCategory.Symbols },
  );
}

/**
 * Emojis matching `query` (name, shortcode or keyword). Recently used ones
 * come first, most recent first; then names starting with the query, then the
 * rest, each in catalog order. An empty query matches nothing.
 */
export function searchEmojis(query: string, recent: readonly string[] = []): EmojiEntry[] {
  const q = query.trim().toLowerCase().replace(/^:|:$/g, '');
  if (!q) return [];
  const words = q.split(/\s+/);
  const rank = new Map(recent.map((e, i) => [e, i]));
  const matches = EMOJI_CATALOG.filter((entry) => {
    const haystack = [entry.name, emojiShortcode(entry), ...entry.keywords];
    return words.every((w) => haystack.some((h) => h.includes(w)));
  });
  const tier = (entry: EmojiEntry) => {
    if (rank.has(entry.emoji)) return 0;
    if (entry.name.startsWith(q) || emojiShortcode(entry).startsWith(q)) return 1;
    return 2;
  };
  return matches
    .map((entry, index) => ({ entry, index, tier: tier(entry) }))
    .sort((a, b) =>
      a.tier - b.tier
      || (a.tier === 0 ? rank.get(a.entry.emoji)! - rank.get(b.entry.emoji)! : 0)
      || a.index - b.index,
    )
    .map((m) => m.entry);
}

/** The catalog grouped by category, in display order. */
export function emojiCategories(): { category: EmojiCategory; label: string; emojis: EmojiEntry[] }[] {
  return Object.values(EmojiCategory).map((category) => ({
    category,
    label: EMOJI_CATEGORY_LABELS[category],
    emojis: EMOJI_CATALOG.filter((e) => e.category === category),
  }));
}
